import memoize from 'fast-memoize'

import {
  BookingPaymentBalanceStatus,
  BookingStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { mainColors, semanticColors, tapeChartColors } from '@avantstay/backoffice-core'

import { LegendOption } from '@new/domains/bookingsTapeChart/BookingsTapeChart.constants'
import {
  BlockedPeriodType,
  BookingsColorMode,
  EconRowType,
} from '@new/domains/fastBookingsTapeChart/__types'

export interface TapeColors {
  primary: string
  secondary: string
}

export const bookingPaymentColors: { [key: string]: TapeColors } = {
  [BookingPaymentBalanceStatus.fullyPaid]: {
    primary: tapeChartColors.fullyPaidPrimaryColor,
    secondary: tapeChartColors.fullyPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.notPaid]: {
    primary: tapeChartColors.notPaidPrimaryColor,
    secondary: tapeChartColors.notPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.overPaid]: {
    primary: tapeChartColors.overPaidPrimaryColor,
    secondary: tapeChartColors.overPaidSecondaryColor,
  },
  [BookingPaymentBalanceStatus.partlyPaid]: {
    primary: tapeChartColors.partiallyPaidPrimaryColor,
    secondary: tapeChartColors.partiallyPaidSecondaryColor,
  },
}

export const bookingStatusColors: { [key: string]: TapeColors } = {
  [BookingStatus.booked]: {
    primary: mainColors.primaryMedium,
    secondary: mainColors.neutralDark,
  },
  [BookingStatus.checkedOut]: {
    primary: semanticColors.successMedium,
    secondary: semanticColors.successExtraLight,
  },
  [BookingStatus.checkedIn]: {
    primary: mainColors.accentDark,
    secondary: mainColors.accentExtremelyLight,
  },
}

export const bookingReservationStatusOptions: LegendOption<BookingStatus>[] = [
  {
    label: 'Checked-In',
    value: BookingStatus.checkedIn,
    dot: bookingStatusColors[BookingStatus.checkedIn].primary,
  },
  {
    label: 'Checked-Out',
    value: BookingStatus.checkedOut,
    dot: bookingStatusColors[BookingStatus.checkedOut].primary,
  },
  {
    label: 'Booked',
    value: BookingStatus.booked,
    dot: bookingStatusColors[BookingStatus.booked].primary,
  },
]

export const bookingPaymentOptions: LegendOption<BookingPaymentBalanceStatus>[] = [
  {
    label: 'Fully Paid',
    value: BookingPaymentBalanceStatus.fullyPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.fullyPaid].primary,
  },
  {
    label: 'Not Paid',
    value: BookingPaymentBalanceStatus.notPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.notPaid].primary,
  },
  {
    label: 'Over Paid',
    value: BookingPaymentBalanceStatus.overPaid,
    dot: bookingPaymentColors[BookingPaymentBalanceStatus.overPaid].primary,
  },
]

export const colorModeOptions = [
  {
    label: 'Reservation status',
    value: BookingsColorMode.ReservationStatus,
  },
  {
    label: 'Payment status',
    value: BookingsColorMode.PaymentStatus,
  },
]

export const blockTypeOptions: LegendOption[] = [
  {
    rowStart: 2,
    label: 'Read-only block',
    value: BlockedPeriodType.ReadOnlyBlock,
    dot: mainColors.neutralExtraDark,
  },
  {
    rowStart: 2,
    label: 'Editable block',
    value: BlockedPeriodType.EditableBlock,
    dot: semanticColors.warningDark,
  },
]

export interface ColorsSet {
  frame: string
  background: string
  backgroundEven: string
  text: string
}

export interface ColorsConfig extends ColorsSet {
  hover: Partial<ColorsSet>
}

export const rowColors: {
  [rowType in EconRowType]: {
    regular: ColorsSet
    hover: Partial<ColorsSet>
    selected: Partial<ColorsSet>
    selectedHover: Partial<ColorsSet>
  }
} = {
  [EconRowType.Normal]: {
    regular: {
      frame: 'transparent',
      background: mainColors.white,
      backgroundEven: mainColors.neutralExtraLight,
      text: mainColors.black,
    },
    hover: {
      background: mainColors.accentExtremelyLight,
      backgroundEven: mainColors.accentExtremelyLight,
      text: mainColors.accentMedium,
    },
    selected: {
      frame: mainColors.accentMedium,
      background: mainColors.accentExtremelyLight,
      backgroundEven: mainColors.accentExtremelyLight,
      text: mainColors.accentMedium,
    },
    selectedHover: {
      frame: mainColors.accentDark,
    },
  },
  [EconRowType.HighRisk]: {
    regular: {
      frame: semanticColors.failureMedium,
      background: '#fff7fa',
      backgroundEven: semanticColors.failureExtraLight,
      text: mainColors.black,
    },
    hover: {
      background: '#fadae4',
      backgroundEven: '#fadae4',
    },
    selected: {
      frame: semanticColors.failureMedium,
      background: '#fadae4',
      backgroundEven: '#fadae4',
    },
    selectedHover: {
      frame: semanticColors.failureDark,
    },
  },
  [EconRowType.OffBoarded]: {
    regular: {
      frame: mainColors.neutralDark,
      background: mainColors.neutralLight,
      backgroundEven: mainColors.neutralLight,
      text: mainColors.neutralDark,
    },
    hover: {
      background: mainColors.neutralMedium,
      backgroundEven: mainColors.neutralMedium,
      text: mainColors.black,
    },
    selected: {
      frame: mainColors.neutralExtraDark,
      background: mainColors.neutralMedium,
      backgroundEven: mainColors.neutralMedium,
      text: mainColors.black,
    },
    selectedHover: {
      frame: mainColors.neutralExtremelyDark,
    },
  },
}

interface RowColorsProps {
  type: EconRowType
  selected: boolean
  isHomeSelectedForQuote: boolean
}

export const getRowColors = memoize(
  ({ type, selected, isHomeSelectedForQuote }: RowColorsProps): ColorsConfig => {
    const colorsForType = rowColors[type]

    if (selected) {
      return {
        ...colorsForType.regular,
        ...colorsForType.selected,
        hover: {
          ...colorsForType.selectedHover,
        },
      }
    }

    if (isHomeSelectedForQuote) {
      return {
        ...colorsForType.regular,
        ...colorsForType.selected,
        frame: 'transparent',
        hover: {
          ...colorsForType.selectedHover,
        },
      }
    }

    return {
      ...colorsForType.regular,
      hover: {
        ...colorsForType.hover,
      },
    }
  },
)
